export type Summary = Record<string, unknown> & {
  earned_by_category?: Record<string, { gross: string; net: string }>
  cash_by_method?: Record<string, { in: string; out: string }>
  checks?: Record<'roll_forward' | 'new_sales_matches_lots' | 'cancelled_matches_refunds' | 'cash_out_matches', boolean>
  balanced?: boolean
  late_count?: number
  unscheduled_gross?: string
  opening_simplecirc_gross?: string
  opening_simplecirc_net?: string
  unscheduled_net?: string
  late_gross?: string
  late_net?: string
}

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

export function money(s: string | null | undefined): string {
  if (s === null || s === undefined || s === '') return '—'
  const n = Number(s)
  if (Number.isNaN(n)) return s
  return n < 0 ? `−${usd.format(-n)}` : usd.format(n)
}

export function monthLabel(period: string): string {
  const [y, m] = period.split('-').map(Number)
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

export function rollForwardRows(s: Summary): { label: string; gross: string; net: string; strong?: boolean }[] {
  const str = (k: string) => s[k] as string | undefined
  const row = (label: string, key: string, strong = false) =>
    ({ label, gross: money(str(`${key}_gross`)), net: money(str(`${key}_net`)), ...(strong ? { strong } : {}) })
  const rows: { label: string; gross: string; net: string; strong?: boolean }[] = [row('Opening deferred', 'opening')]
  if (Number(str('opening_simplecirc_gross') ?? 0) !== 0 || Number(str('opening_simplecirc_net') ?? 0) !== 0) {
    rows.push({ label: '  of which Opening from SimpleCirc', gross: money(str('opening_simplecirc_gross')), net: money(str('opening_simplecirc_net')) })
  }
  rows.push(
    row('+ New sales', 'new_sales'),
    row('− Earned', 'earned'),
    row('− Refunds, voids and returns', 'cancelled'),
    row('− Written off', 'written_off'),
    row('= Closing deferred', 'closing', true),
  )
  const un = Number(str('unscheduled_gross') ?? 0)
  if (un !== 0) {
    rows.push({ label: '  of which unscheduled (no months recorded)', gross: money(str('unscheduled_gross')), net: money(str('unscheduled_net')) })
  }
  return rows
}

export const CHECK_LABELS: Record<string, string> = {
  roll_forward: 'Opening plus new sales, less earned, refunds and write-offs, does not equal closing',
  new_sales_matches_lots: 'New sales do not match the payment lots',
  cancelled_matches_refunds: 'Refunds, voids and returns do not match the refund lots',
  cash_out_matches: 'Cash refunded does not match the refund lots',
}

export function failedChecks(s: Summary): string[] {
  return Object.entries(s.checks ?? {}).filter(([, ok]) => !ok).map(([k]) => CHECK_LABELS[k] ?? k)
}

export const IN_PROGRESS = 'in progress'

/** The current month: live figures, not closeable until it ends. */
export function isInProgress(status: string): boolean {
  return status === IN_PROGRESS
}

export function statusTone(status: string): 'ok' | 'warn' | 'live' | 'muted' {
  if (status === 'approved') return 'ok'
  if (status === 'draft') return 'warn'
  if (isInProgress(status)) return 'live'
  return 'muted'
}

const CATEGORY_LABELS: Record<string, string> = { opening: 'SimpleCirc prepaid' }

/** Earned-by-type keys in plain words ('opening' is the SimpleCirc-seeded balance). */
export function categoryLabel(key: string): string {
  return CATEGORY_LABELS[key] ?? key
}

export const ACCOUNT_LABELS: Record<string, string> = {
  deferred_revenue: 'Deferred revenue (liability)',
  subscription_revenue: 'Subscription revenue',
  agency_commission_expense: 'Agency commission expense',
  sales_tax_payable: 'Sales tax payable',
  cash_authnet: 'Cash clearing — Authorize.Net',
  cash_paypal: 'Cash clearing — PayPal',
  cash_stripe: 'Cash clearing — Stripe',
  cash_check: 'Cash clearing — Checks',
  cash_aas: 'Cash clearing — Air Age Store',
  cash_agency: 'Cash clearing — Agencies',
  cash_other: 'Cash clearing — Other',
}

/** Same name the server sends: a month that is not approved yet downloads as -DRAFT. */
export function journalFileName(slug: string, period: string, approved: boolean): string {
  return `${slug}-${period}-journal${approved ? '' : '-DRAFT'}.csv`
}
